import { useEffect, useRef, useState } from "react";
import { Alert, Group, Progress, Stack, Text } from "@mantine/core";
import { IconSparkles, IconAlertTriangle } from "@tabler/icons-react";
import { jsonGet } from "../api";

const STAGE_LABELS = {
  NotStarted: { label: "Queued — discovery starting…", progress: 0.05 },
  FetchingWebsite: { label: "Fetching your website…", progress: 0.15 },
  InferringTopics: { label: "Inferring topics, keywords, and competitors…", progress: 0.35 },
  ResearchingKeywords: { label: "Researching keywords…", progress: 0.65 },
  ExtractingVisualStyle: { label: "Extracting your visual style…", progress: 0.80 },
  Finalizing: { label: "Finalizing your brand profile…", progress: 0.92 },
  Done: { label: "Discovery complete", progress: 1.0 },
  Failed: { label: "Discovery failed", progress: 0 },
};

const POLL_INTERVAL_MS = 2000;

/**
 * Polls a brand's discovery job and shows staged progress. Calls onComplete() when the
 * job reaches Success/Failed status so the parent can refresh the brand record.
 */
export default function BrandDiscoveryProgressCard({ brand, onComplete }) {
  const jobId = brand.currentDiscoveryJobId;
  const stage = brand.discoveryStage ?? "NotStarted";
  const isFailed = stage === "Failed";
  const isDone = stage === "Done";

  const [jobProgress, setJobProgress] = useState(null);
  const [pollError, setPollError] = useState(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!jobId || isDone || isFailed) return undefined;

    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const job = await jsonGet(`jobs/${jobId}`);
        if (cancelled) return;
        setJobProgress(job.progress ?? null);
        setPollError(null);

        // JobStatus enum: Queued=0, Processing=10, Success=20, Failed=30
        if (job.status === 20 || job.status === 30) {
          onCompleteRef.current?.();
          return;
        }
      } catch (e) {
        if (cancelled) return;
        setPollError(e?.message ?? "Failed to fetch job status");
      }

      if (!cancelled) timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId, isDone, isFailed]);

  if (!jobId && !isFailed) return null;

  if (isFailed) {
    return (
      <Alert
        color="red"
        icon={<IconAlertTriangle size={18} />}
        title="Discovery failed"
        mb="md"
      >
        <Stack gap="xs">
          <Text size="sm">
            We couldn't auto-discover this brand. {brand.setupNotes ? brand.setupNotes : "You can fill in the details manually below, then re-run detection."}
          </Text>
        </Stack>
      </Alert>
    );
  }

  const stageInfo = STAGE_LABELS[stage] ?? STAGE_LABELS.NotStarted;
  // Prefer the live job progress (smooth updates) but fall back to the stage's nominal value.
  const displayProgress = Math.max(
    Math.round((jobProgress ?? stageInfo.progress) * 100),
    Math.round(stageInfo.progress * 100),
  );

  return (
    <Alert
      color="blue"
      icon={<IconSparkles size={18} />}
      title="Discovering your brand"
      mb="md"
    >
      <Stack gap="xs">
        <Group justify="space-between" gap="sm">
          <Text size="sm">{stageInfo.label}</Text>
          <Text size="xs" c="dimmed">
            {displayProgress}%
          </Text>
        </Group>
        <Progress value={displayProgress} animated striped />
        <Text size="xs" c="dimmed">
          This usually takes 10–30 seconds. You can leave this page — we'll keep working in the background.
        </Text>
        {pollError && (
          <Text size="xs" c="orange">
            Status check failed; retrying… ({pollError})
          </Text>
        )}
      </Stack>
    </Alert>
  );
}
